import QuestionQrCode from "./QuestionQrCode";
import QuestionMediaRenderer from "./QuestionMediaRenderer";
import { baseLanguageCode, normalizeDisplayedLineBreaks } from "../../../utils/multilineText";

type Props = {
  questionData: any;
  currentLanguage: string;
  isSecondChanceActive: boolean;
  getPreviewTimerSeconds: () => number;
  SECOND_CHANCE_SECONDS: number;
  setOptionImagePreviewUrl: (url: string | null) => void;
  getLocalizedField: (value: unknown, lang: string) => string;
  t: any;
};

const GameAnswerQuestionContent = ({
  questionData,
  currentLanguage,
  isSecondChanceActive,
  getPreviewTimerSeconds,
  SECOND_CHANCE_SECONDS,
  setOptionImagePreviewUrl,
  getLocalizedField,
  t,
}: Props) => {
  const questionLang = baseLanguageCode(currentLanguage);
  const questionText = normalizeDisplayedLineBreaks(getLocalizedField(questionData?.question, questionLang));

  return (
    <div className="w-full flex-1 2xl:rounded-[1.04vw] rounded-2xl 2xl:border-[0.2vw] xl:border-3 border-2 border-brown-primary bg-brown-primary 2xl:p-[0.04vw] 2xl:px-[0.1vw] xl:p-[0.1vw] p-[0.02vw] flex 2xl:gap-[1vw] xl:gap-4 md:gap-3 gap-2 overflow-hidden sm:flex-row flex-col">
      <div className="bg-white 2xl:border-[0.208vw] xl:border-4 border-2 border-brown-primary w-full  2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1vw] p-3 landscape:max-lg:p-2 overflow-hidden sm:h-full flex-1">
        <div className="w-full mx-auto flex flex-col items-center justify-center 2xl:min-h-[10vw] xl:min-h-[calc(100%-94px)] min-h-[calc(100%-32px)] h-full">
          {questionData?.categoryName && (
            <p dir={currentLanguage === "ar" ? "rtl" : "ltr"} className="text-primary-violet font-garamond font-semibold 2xl:text-[1.5vw] xl:text-md md:text-lg text-base mb-2 landscape:max-lg:mb-1 uppercase tracking-wide">
              {typeof questionData.categoryName === "object"
                ? questionData.categoryName[currentLanguage] || questionData.categoryName.en
                : questionData.categoryName}
            </p>
          )}
          <h2
            dir={questionLang === "ar" ? "rtl" : "ltr"}
            className="2xl:text-[2.5625vw]/[2.75vw] xl:text-4xl/[2.6rem] md:text-3xl/[2.1rem] text-2xl/[1.75rem] font-garamond font-bold text-center whitespace-pre-line wrap-break-word [unicode-bidi:plaintext]"
          >
            {questionText}
          </h2>
          {questionData?.question_type !== "qr_code" && (
            <QuestionMediaRenderer
              media={questionData?.media}
              mediaType={questionData?.question_type}
              showPreviewTimer={!!(questionData?.time_limit || questionData?.question_type === "qr_code")}
              previewTimerElapsedMode={!isSecondChanceActive}
              getPreviewTimerSeconds={getPreviewTimerSeconds}
              previewTimerInitialTime={isSecondChanceActive ? SECOND_CHANCE_SECONDS : undefined}
            />
          )}
          {questionData?.question_type === "qr_code" && (
            <QuestionQrCode
              data={questionData?.media ? questionData?.media : questionData?.answer}
              type={questionData?.media ? "media" : "word"}
              enableHideToggle={true}
            />
          )}
        </div>
      </div>
      {questionData?.options && questionData.options.length > 0 && (
        <div
          className={`sm:h-full h-fit justify-center items-center flex flex-col 2xl:gap-[0.625vw] gap-2 pe-3 ${questionData?.option_type === "image"
            ? "2xl:max-w-[24vw] xl:max-w-[420px] "
            : "2xl:max-w-[15.625vw] xl:max-w-[300px] lg:max-w-52 md:max-w-44 sm:max-w-40 w-full"
            }`}
        >
          {questionData?.option_type === "image" ? (
            <div className="grid grid-cols-2 gap-2 w-full place-items-center">
              {questionData.options.map((option: any, index: number) => {
                const optionUrl = getLocalizedField(option, currentLanguage);
                return (
                  <button
                    key={option?._id || index}
                    type="button"
                    onClick={() => optionUrl && setOptionImagePreviewUrl(optionUrl)}
                    className="shrink-0 w-[100px] h-[80px] rounded-lg border-[1.5px] border-black-300 bg-white overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-95 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                    aria-label={t("gameAnswer.previewOptionImage") || "Preview image"}
                  >
                    {optionUrl ? (
                      <img src={optionUrl} alt="" className="w-full h-full object-contain" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            questionData.options.map((option: any, index: number) => (
              <button
                key={option._id || index}
                className="w-full skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
              >
                <span dir={currentLanguage === "ar" ? "rtl" : "ltr"} className="skew-x-10 2xl:mt-[0.208vw] mt-1 whitespace-normal wrap-break-words">
                  {typeof option === "object" ? option[currentLanguage] || option.en : option}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GameAnswerQuestionContent;
